import React, { useState, useMemo } from "react";
import { Container, Button, Spinner } from "reactstrap";
import styles from "../css/adverseEvent.module.scss";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import AESummaryCards from "../components/AESummaryCards";
import { useAdverseEventApi } from "../api/useAdverseEventApi";
import { useNavigate } from "react-router-dom";
import { MASTER_AE_PATHS } from "../../../../Routes/paths";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import {
  formatDate,
  GetUserData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import CommonButton from "../../../../Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import { adverseEventColumns } from "../helper/adverseeventconstant";
import { AdverseEvent } from "../helper/adverseevent.types";
import { useAuditService } from "../../../../modules/Audit/api/useAuditQuery";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "context/StudyContext";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";
import PinVerificationModal from "../../../Pharmacy/DailyReconsilation/components/PinVerificationModal";
import { queryClient } from "api/queryClient";

const AdverseEventList: React.FC = () => {
  document.title = "Adverse Event | ZENTIXS VISTA";
  const navigate = useNavigate();
  const userData = GetUserData();

  const [searchText, setSearchText] = useState<string | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const { selectedStudy: globalstudy } = useGlobalStudy();

  const { SelectAllQuery } = useAdverseEventApi({
    searchtext: searchText,
    pagenumber: currentPageNumber,
    pagesize: 5,
    status: "A",
    studyid: Number(globalstudy?.studyid),
  });
  const { DeleteMutation, VerifyMutation } = useAdverseEventApi();

  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [isPinInvalid, setIsPinInvalid] = useState<boolean>(false);
  //-------------------------------------------------------------------------------------------
  //--> On Edit
  //-------------------------------------------------------------------------------------------
  const onEdit = (item: AdverseEvent) => {
    navigate(`/adverse-event/edit/${item.id}`, { state: item });
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Adverse Event",
      studyid: globalstudy?.studyid,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Adverse_Event_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", error);
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const onView = (item: AdverseEvent) => {
    navigate(`/adverse-event/view/${item.id}`);
  };

  const columns = useMemo(
    () => [
      {
        key: "adverseeventnumber",
        label: "Event ID",
        width: "140px",
        type: "link" as const,
        onClick: (row: any) => onView(row),
      },
      ...adverseEventColumns.slice(1),
    ],
    [navigate]
  );

  const adverseEventData = SelectAllQuery.data?.data || [];
  const totalRecords = SelectAllQuery.data?.data[0]?.total_count || 0;
  let formattedList = adverseEventData.map((item: any) => {
    return {
      ...item,
      onsetdate: formatDate(item.onsetdate),
    };
  });

  const onSearch = (value: string | null) => {
    setSearchText(value);
    setCurrentPageNumber(1);
  };

  const [deleteModal, setDeleteModal] = useState(false);
  const [showDeleteReason, setShowDeleteReason] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const onDeleteClick = (row: any) => {
    setSelectedRecord(row);
    setDeleteModal(true);
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    setShowDeleteReason(false);
    setCurrentPageNumber(1);
  };

  const handleVerifyClick = (row: any) => {
    setSelectedRecord(row);
    setVerifyModal(true);
    setIsPinInvalid(false);
  };

  const handlePinVerify = (password: string) => {
    if (!selectedRecord) return;
    setIsPinInvalid(false);

    const payload = {
      adverseeventid: Number(selectedRecord.id),
      verifiedby: Number(userData?.userid),
      verifypin: password,
    };

    VerifyMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message);
        queryClient.invalidateQueries({
          queryKey: ["verification-notification-count"],
        });
        setVerifyModal(false);
        setSelectedRecord(null);
        SelectAllQuery.refetch();
      },
      onError: (err: any) => {
        const errorMsg = typeof err === "string" ? err : err?.message ?? "";
        if (errorMsg.toLowerCase().includes("incorrect password")) {
          setIsPinInvalid(true);
        } else {
          setIsPinInvalid(false);
        }
        showToastMessage("error", errorMsg || err);
      },
    });
  };

  const getRowActions = (row: any): any => {
    const isVerified = row?.isverified;
    const actions: any = {};

    if (!isVerified) {
      actions.onVerify = (r: any) => handleVerifyClick(r);
      actions.onEdit = onEdit;
      actions.onDelete = onDeleteClick;
    }
    return actions;
  };

  const handleDelete = (payload: { id: number | null; reason: string }) => {
    const request = {
      adverseeventid: payload.id,
      updatereason: payload.reason,
    };

    DeleteMutation.mutate(request, {
      onSuccess: (res) => {
        showToastMessage("success", res?.message);
        queryClient.invalidateQueries({
          queryKey: ["verification-notification-count"],
        });
        toggleDeleteModal();
        setSelectedRecord(null);
        SelectAllQuery.refetch();
        setSearchText("");
        const totalRecords = formattedList[0]?.total_count || 0;
        const lastPage = Math.ceil((totalRecords - 1) / 5);

        setCurrentPageNumber(
          Math.max(1, Math.min(currentPageNumber, lastPage)),
        );
      },
    });
  };
  const statsData = SelectAllQuery.data?.data?.[0];
  const stats = {
    totalReported: statsData?.total_reported_adverse || 0,
    ongoingFollowups: statsData?.total_ongoing_followups || statsData?.ongoing_followups || 0,
    seriousAEs: statsData?.total_serious_adverse || 0,
    pendingReview: statsData?.total_pending_review || statsData?.total_unverified || 0,
  };
  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>
        {!globalstudy?.studyid ? (
          <div>
            <NoStudySelectedPlaceholder moduleName="adverse events" />
          </div>
        ) : (
          <>
            {/* Header Section */}
            <div className={`d-flex justify-content-between align-items-center mb-2 px-1 ${styles.headerContainer}`}>
              <div className={styles.headerSection}>
                <h1 className="fw-600 mb-1 fs-18">
                  Adverse Event Identification and Reporting
                </h1>
                <p className="text-muted mb-0">
                  Monitor , log and report adverse events across all clinical
                  trials.
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.ADVERSE_EVENT}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600 "
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  module={PERMISSION_MODULES.ADVERSE_EVENT}
                  action={PERMISSION_ACTIONS.ADD}
                  color="primary"
                  outline
                  size="sm"
                  className="px-2 fw-600"
                  onClick={() => navigate(MASTER_AE_PATHS.ADD)}
                >
                  <i className="ri-add-line" /> Log New Adverse Event
                </CommonButton>
              </div>
            </div>

            {/* Summary Cards */}
            <AESummaryCards stats={stats} loading={SelectAllQuery?.isLoading} />

            {/* Main Content Grid */}
            <div className={styles.mainGrid}>
              {/* Recent Events Table */}
              <div className={styles.listPanel}>
                <div className={styles.panelHeader}>
                  <h2>
                    <i className="ri-list-check-2 me-2 text-primary fs-18" />
                    Recent Adverse Events
                  </h2>
                  <CommonSearch
                    onSearch={onSearch}
                    searchText={searchText ?? ""}
                  />
                </div>
                <div className="p-3">
                  <CommonTableIndexWise
                    moduleName={PERMISSION_MODULES.ADVERSE_EVENT}
                    columns={columns}
                    data={formattedList}
                    loading={SelectAllQuery.isFetching}
                    pageSize={5}
                    currentPage={currentPageNumber}
                    onPageChange={setCurrentPageNumber}
                    totalRecords={totalRecords}
                    actions={getRowActions}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </Container>
      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
      <DeleteModal
        isopen={deleteModal}
        toggle={toggleDeleteModal}
        recordId={selectedRecord?.id}
        recordName={selectedRecord?.eventterm}
        onDeleteClick={handleDelete}
        isLoading={DeleteMutation.isPending}
        showDeleteReason={showDeleteReason}
        toggleDeleteReason={() => setShowDeleteReason(!showDeleteReason)}
      />
      <PinVerificationModal
        isOpen={verifyModal}
        toggle={() => setVerifyModal(false)}
        onVerify={handlePinVerify}
        isLoading={VerifyMutation.isPending}
        title="E-Signature — Adverse Event"
        description={
          <>
            Please enter your password to apply your E-Signature for the adverse event:{" "}
            <strong>{selectedRecord?.eventterm}</strong>
          </>
        }
        isInvalid={isPinInvalid}
      />
    </div>
  );
};

export default AdverseEventList;
